import CheckBox from "../../../components/common/checkBox";
import { ReactComponent as Dropdown } from "../../../assets/icons/dropdown.svg";
import { ReactComponent as PrintOn } from "../../../assets/icons/printOn.svg";
import { OrderData } from "../../../../common/hooks/console/transaction/useGetOrder";
import { ReactComponent as PrintOff } from "../../../assets/icons/printOff.svg";
import { ReactComponent as Empty } from "../../../../common/assets/icons/empty.svg";
import { useEffect, useState } from "react";
import Button from "common/components/button";
import PaymentPop from "console/components/popup/PaymentPop";
import { useSystemPopStore } from "console/store/useSystemPopStore";
import useToastStore from "console/store/useToastStore";
import { makeIntComma } from "common/hooks/utils/utils";
import { api_uri } from "common/hooks/utils/enum";
import { usePostOrderPhotoCheck } from "common/hooks/console/transaction/usePostOrderPhotoCheck";

// 준비완료 사진 확인 대상 지점. 이 지점 발주 건만 사진미확인 점멸 대상이다
const PHOTO_CHECK_BRANCH = ["베스트플라워", "원일"];

type TransactionTableProps = {
    data?: OrderData[];
    tableClick?: string | null;
    /* 가운데 영역이 좁을 때 쓰는 좁은 칸 폭. 넓으면 기본 폭(명의·판매금액 위치 요청으로 되돌린 값) */
    compact?: boolean;
    selectedRow: (id: number) => void;
    selectedRows: (selectedIds: number[]) => void;
    clearTrigger: boolean;
    onClearComplete: () => void;
    activeTabId?: number | null;
    field: (field: string) => void;
};

const TransactionTable = ({
    data = [],
    tableClick,
    compact = false,
    selectedRow,
    selectedRows,
    clearTrigger,
    onClearComplete,
    activeTabId,
    field,
}: TransactionTableProps) => {
    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
    const isAllSelected = data.length > 0 && selectedOrders.length === data.length;
    const [paymentPop, setPaymentPop] = useState<boolean>(false);
    const [orderIdx, setOrderIdx] = useState<number | null>(null);
    const [customerIdx, setCustomerIdx] = useState<number | null>(null);
    /* 지점 준비완료 사진 크게 보기 */
    const [readyPop, setReadyPop] = useState<{ images: string[]; sender: string; word: string } | null>(null);
    const { mutate: photoCheckMutate } = usePostOrderPhotoCheck();
    const { showPop } = useSystemPopStore();
    const { addToast } = useToastStore();

    const handleSort = (fieldName: string) => {
        setSortOrder(prev => (prev === "ASC" ? "DESC" : "ASC"));
        field(`${fieldName},${sortOrder}`);
    };

    const handleRowSelect = (orderIdx: number) => {
        setSelectedOrders(prevSelected => {
            const updatedSelection = prevSelected.includes(orderIdx)
                ? prevSelected.filter(id => id !== orderIdx)
                : [...prevSelected, orderIdx];

            selectedRows(updatedSelection);
            return updatedSelection;
        });
    };

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedOrders([]);
            selectedRows([]);
        } else {
            const allIds = data.map(product => product.order_idx);
            setSelectedOrders(allIds);
            selectedRows(allIds);
        }
    };

    useEffect(() => {
        if (clearTrigger) {
            setSelectedOrders([]);
            selectedRows([]);
            onClearComplete();
        }
    }, [clearTrigger, selectedRows, onClearComplete]);

    return (
        /* 가로 스크롤은 바깥(헤더와 같이 움직이는 영역)이 맡는다 */
        <div className="h-full overflow-y-auto w-full">
            <table className="table-auto w-full text-sm min-w-[100px]">
                {/* Header */}
                <thead className="bg-primary3 border-b border-gray3 shadow-md h-[60px]">
                    <tr>
                        <th className="border-b border-b-gray3 border-r border-r-gray4 px-3 w-[40px]">
                            <CheckBox checked={isAllSelected} onChange={handleSelectAll} />
                        </th>
                        <th
                            className={`border-b ${compact ? "min-w-[150px]" : "min-w-[210px]"} border-b-gray3 border-r border-r-gray4 px-3 text-center`}
                        >
                            <div className="flex flex-col gap-1 items-center">
                                <div className="flex items-center gap-1 ml-1">
                                    <p className="text-base">배송일자</p>
                                    <p className="text-gray2 font-normal">배송시간</p>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("delivery_date")} />
                                </div>
                                <p className="text-gray2 font-normal">주문상태 및 인수증 출력</p>
                            </div>
                        </th>
                        <th className="border-b border-b-gray3 font-normal border-r border-r-gray4 w-full">
                            <div className="flex items-center h-[30px] w-full">
                                <div
                                    className={`flex items-center h-[30px] px-2 border-r border-gray4 justify-between ${compact ? "min-w-[180px]" : "min-w-[250px]"}
                                    }`}
                                >
                                    <span>주문자</span>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("order_name")} />
                                </div>
                                <div
                                    /*
                                    // 좁은 폭에서도 상품/배송주소는 300px 아래로 줄이지 않는다. 다른 칸보다 먼저 줄어
                                    // 가로 스크롤이 생기는 최소 폭에서 주소가 몇 글자만 보였다. (기본 폭 경계 1400px 일 때 약 310px)
                                    */
                                    className={`flex px-2 ${compact ? "min-w-[300px]" : "min-w-[220px]"} w-full justify-between items-center`}
                                >
                                    <span className="whitespace-nowrap">상품</span>
                                    <Dropdown className="cursor-pointer" onClick={() => handleSort("product_name")} />
                                </div>
                            </div>
                            <div className="flex h-[30px] font-normal items-center border-t border-gray4">
                                <p
                                    className={`${compact ? "min-w-[180px]" : "min-w-[250px]"} h-full flex items-center px-2 border-r border-gray4 justify-start`}
                                >
                                    명의
                                </p>
                                <p className="h-[30px] pt-1 flex justify-start px-2 border-t border-gray4">배송주소</p>
                            </div>
                        </th>
                        <th className={`${compact ? "min-w-[360px]" : "min-w-[600px]"}`}>
                            <div className="flex h-[30px] font-normal">
                                <div
                                    className={`px-2 h-full flex items-center justify-start ${compact ? "min-w-[80px]" : "min-w-[130px]"} border-r`}
                                >
                                    판매금액
                                </div>
                                <div className="px-2 h-full flex items-center justify-center flex-1 border-r">발주상태</div>
                                <div
                                    className={`px-2 h-full flex items-center justify-start ${compact ? "min-w-[80px]" : "min-w-[130px]"} border-r`}
                                >
                                    발주금액
                                </div>
                                <div
                                    className={`px-2 h-full flex items-center justify-center ${compact ? "min-w-[110px]" : "min-w-[160px]"} border-r`}
                                >
                                    입금상태
                                </div>
                            </div>
                            <div className="flex h-[30px] font-normal items-center border-t border-gray4">
                                <div className="w-1/3 h-full flex items-center px-2 justify-start">받으시는 분</div>
                            </div>
                        </th>
                    </tr>
                </thead>

                {/* Body */}
                <tbody>
                    {data.length > 0 ? (
                        data?.map(product => (
                            <tr
                                key={product.order_idx}
                                className={`cursor-pointer group hover:bg-[#E6EEDD] ${
                                    tableClick === "TransactionDetail" && activeTabId === product.order_idx ? "bg-[#F9EBEB]" : ""
                                }`}
                                onClick={() => selectedRow(product.order_idx)}
                            >
                                <td className="border-b border-gray4 " onClick={e => e.stopPropagation()}>
                                    <CheckBox
                                        className="flex justify-center"
                                        checked={selectedOrders.includes(product.order_idx)}
                                        onChange={() => handleRowSelect(product.order_idx)}
                                    />
                                </td>
                                <td className={`border border-gray4 ${compact ? "px-2" : "px-3"} text-center py-2 vertical-top`}>
                                    <div className="flex flex-col text-gray2 gap-1 items-center">
                                        <div className="flex gap-1 items-center">
                                            <p
                                                className={`text-[15px] font-bold whitespace-nowrap ${product.m_id ? "text-gray1" : "text-[#EA580C]"}`}
                                            >
                                                {product.order_number}
                                            </p>
                                            <p className="whitespace-nowrap">{product.delivery_date?.replace(/\s*\/\s*/g, "/")}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div
                                                className={`py-[3px] text-[13px] whitespace-nowrap rounded-3xl px-1.5 font-bold ${
                                                    product.order_status === 1
                                                        ? "bg-[#FFC700] text-white  px-[14px]"
                                                        : product.order_status === 2
                                                          ? "border border-success text-success"
                                                          : product.order_status === 3
                                                            ? "border border-warning text-warning"
                                                            : product.order_status === 4
                                                              ? "border border-skyblue text-skyblue"
                                                              : product.order_status === 5
                                                                ? "border border-grayicon text-grayicon"
                                                                : product.order_status === 6
                                                                  ? "bg-gray4 px-5 text-gray3"
                                                                  : product.order_status === 7
                                                                    ? "bg-error px-5 text-white"
                                                                    : ""
                                                }`}
                                            >
                                                {product.order_status === 1
                                                    ? "미확인"
                                                    : product.order_status === 2
                                                      ? "접수완료"
                                                      : product.order_status === 3
                                                        ? "준비완료"
                                                        : product.order_status === 4
                                                          ? "배송완료"
                                                          : product.order_status === 5
                                                            ? "주문취소"
                                                            : product.order_status === 6
                                                              ? "보류"
                                                              : "삭제"}
                                            </div>
                                            {product.is_printed ? <PrintOn /> : <PrintOff />}
                                            <p className={`text-[13px] ${compact ? "truncate" : "whitespace-nowrap"}`}>
                                                {product.customer_manager_name}
                                            </p>
                                        </div>
                                        <div className="text-sm font-bold translate-y-[3px] flex items-center">
                                            청구일자 {product.excel_date ? product.excel_date : "-"}
                                        </div>
                                    </div>
                                </td>
                                <td className="border border-gray4 text-left max-w-[650px]">
                                    <div className="flex min-h-12 border-b border-gray4">
                                        <div
                                            className={`border-r font-bold min-h-12 border-gray4 py-1 px-2 ${compact ? "w-[180px]" : "w-[250px]"}`}
                                        >
                                            <p className="">{product.customer_name}</p>
                                            <p className="text-gray2">
                                                {product.order_name} {product.order_mobile}
                                            </p>
                                        </div>
                                        <div className="flex p-2 w-20 flex-grow flex-wrap">
                                            <span className="text-gray2">
                                                {product.product_name} ({makeIntComma(product.product_quantity || 0)}개){" "}
                                                {product.customer_request}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center text-gray2 min-h-12 grid ${compact ? "grid-cols-[180px,_1fr]" : "grid-cols-[250px,_1fr]"}`}
                                    >
                                        <p
                                            // ${tableClick ? "min-w-[280px] max-w-[280px]" : "min-w-[280px] max-w-[280px]"}
                                            className={` h-full border-r border-gray4 flex items-center p-2 justify-start`}
                                        >
                                            {product.wreath_sender}
                                        </p>
                                        <p
                                            className="p-2 h-full"
                                            // style={{
                                            //     display: "-webkit-box",
                                            //     WebkitLineClamp: 2,
                                            //     WebkitBoxOrient: "vertical",
                                            //     overflow: "hidden",
                                            //     textOverflow: "ellipsis",
                                            // }}
                                        >
                                            {product.delivery_address}
                                            {product.delivery_address_detail
                                                ? "" + (product?.delivery_address_detail ? " " + product.delivery_address_detail : "")
                                                : ""}
                                        </p>
                                    </div>
                                </td>
                                <td className="border-b align-top">
                                    <div className="h-full flex flex-col">
                                        <div className="flex">
                                            <div
                                                className={`border-r truncate px-2 border-gray4 justify-end items-center flex ${compact ? "min-w-[80px]" : "min-w-[130px]"} 
        ${tableClick === "TransactionDetail" && activeTabId === product.order_idx ? "bg-[#F9EBEB]" : "bg-primary4"} 
        group-hover:bg-[#E6EEDD]`}
                                            >
                                                {product.sales_amount?.toLocaleString()}원
                                            </div>
                                            <div
                                                className={`${product.branch_name === "본점" ? "text-warning" : "text-primary2"}  font-bold px-2 flex flex-col gap-1 flex-1 justify-center items-center min-h-12 border-r border-gray4`}
                                            >
                                                {/* 지점명과 준비완료 사진은 한 줄에 둔다 */}
                                                <div className="flex items-center gap-1 whitespace-nowrap">
                                                    {/* 사진 미확인 건은 지점명 테두리도 함께 점멸한다 */}
                                                    <span
                                                        className={
                                                            product.order_status === 3 &&
                                                            PHOTO_CHECK_BRANCH.includes(product.branch_name) &&
                                                            !product.ready_by_admin &&
                                                            !product.photo_checked
                                                                ? "border-2 rounded-md px-1.5 py-0.5 animate-blink-border"
                                                                : ""
                                                        }
                                                    >
                                                        {tableClick
                                                            ? product.branch_name?.length > 11
                                                                ? product.branch_name.substring(0, 11) + "..."
                                                                : product.branch_name
                                                            : product.branch_name}
                                                    </span>
                                                    {/* 지점이 올린 준비완료 사진. 준비완료 상태인 동안 테두리가 점멸한다 */}
                                                    {!!product.ready_images?.length && (
                                                        <img
                                                            alt="준비완료"
                                                            src={`${api_uri}/${product.ready_images[0]}`}
                                                            className={`w-[26px] h-[26px] shrink-0 object-cover rounded-md border-2 cursor-pointer ${
                                                                product.order_status === 3 &&
                                                                PHOTO_CHECK_BRANCH.includes(product.branch_name) &&
                                                                !product.ready_by_admin &&
                                                                !product.photo_checked
                                                                    ? "animate-blink-border"
                                                                    : "border-gray4"
                                                            }`}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setReadyPop({
                                                                    images: product.ready_images || [],
                                                                    sender: product.wreath_sender || "",
                                                                    word: product.wreath_word || "",
                                                                });
                                                                // 확인 기록을 남기면 목록이 갱신되며 점멸이 멈춘다
                                                                if (
                                                                    PHOTO_CHECK_BRANCH.includes(product.branch_name) &&
                                                                    !product.ready_by_admin &&
                                                                    !product.photo_checked
                                                                ) {
                                                                    photoCheckMutate(String(product.order_idx));
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {/* 지점이 자기 화면에서 숨긴 건. 본점 데이터는 그대로다 */}
                                                {product.branch_del === "Y" && (
                                                    <span className="bg-gray3 text-white text-[11px] font-normal rounded-3xl px-1.5 py-0 leading-[16px] whitespace-nowrap">
                                                        {product.branch_name}삭제
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className={`border-r truncate px-2 border-gray4 justify-end items-center flex ${compact ? "min-w-[80px]" : "min-w-[130px]"} 
        ${tableClick === "TransactionDetail" && activeTabId === product.order_idx ? "bg-[#F9EBEB]" : "bg-primary4"} 
        group-hover:bg-[#E6EEDD]`}
                                            >
                                                {product.order_amount.toLocaleString()}원
                                            </div>
                                            <div
                                                className={`px-2 justify-center items-center flex ${compact ? "min-w-[110px]" : "min-w-[160px]"}`}
                                            >
                                                <span
                                                    className={`justify-center flex ${compact ? "min-w-[45px] whitespace-nowrap" : "w-[65px]"} ${product.payment_status_text === "미수" ? "text-grayicon" : "text-skyblue"}`}
                                                >
                                                    {product.payment_status_text}
                                                </span>

                                                {product.payment_status_text === "미수" && (
                                                    <Button
                                                        onClick={() => {
                                                            if (product.payment_status_text === "미수") {
                                                                setPaymentPop(true);
                                                                setOrderIdx(product.order_idx);
                                                                setCustomerIdx(product.customer_idx);
                                                            }
                                                        }}
                                                        size="S"
                                                        status={`${product?.payment_status_text === "미수" ? "regist" : ""}`}
                                                        className="px-2 whitespace-nowrap"
                                                    >
                                                        {product?.payment_status_text === "미수" ? "입금등록" : "입금취소"}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex w-full h-full border-t border-gray4">
                                            <div className="px-2 min-h-12 flex items-center">
                                                {product.recipient_name} {"  "}
                                                {product.event_content.depth2
                                                    ? product.event_content.depth2
                                                    : product.event_content.depth1 === "직접입력"
                                                      ? product.event_content.event_content
                                                      : ""}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className="text-center">
                                <div className="flex flex-col items-center gap-5 py-4 mt-52">
                                    <Empty />
                                    <p className="text-grayicon text-lg">등록된 내역이 없습니다.</p>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {paymentPop && customerIdx && orderIdx && (
                <PaymentPop
                    customerIdx={customerIdx}
                    orderIdx={orderIdx}
                    onClose={() => {
                        setPaymentPop(false);
                        setOrderIdx(null);
                        setCustomerIdx(null);
                    }}
                    handleSubmitCallback={() => {
                        addToast({
                            type: "success",
                            message: "입금 내역을 등록하었습니다.",
                        });
                    }}
                    handleErrorCallback={errorMessage => {
                        showPop({
                            title: errorMessage,
                            rightButton: undefined,
                            type: "negative",
                            onRightClick: () => {},
                        });
                    }}
                />
            )}

            {/* 지점 준비완료 사진 크게 보기 - 명의·경조사어를 함께 보여준다 */}
            {readyPop && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200] p-6"
                    onClick={() => setReadyPop(null)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-custom max-w-[90vw] max-h-[90vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray4">
                            <p className="text-xl font-bold">준비완료 사진</p>
                            <button className="text-gray3 text-2xl leading-none" onClick={() => setReadyPop(null)}>
                                ×
                            </button>
                        </div>

                        <div className="flex flex-col gap-2 px-6 py-4 border-b border-gray4 text-sm">
                            <p>
                                <span className="text-gray3">명의</span>{" "}
                                <span className="font-bold whitespace-pre-wrap">{readyPop.sender || "-"}</span>
                            </p>
                            <p>
                                <span className="text-gray3">경조사어</span> <span className="font-bold">{readyPop.word || "-"}</span>
                            </p>
                        </div>

                        <div className="flex gap-3 p-6 overflow-auto justify-center flex-wrap">
                            {readyPop.images.map((image, index) => (
                                <img
                                    key={index}
                                    alt={`준비완료 ${index + 1}`}
                                    src={`${api_uri}/${image}`}
                                    className="max-w-[70vw] max-h-[60vh] object-contain rounded-xl"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionTable;
